import type { PreparedShare, ShareOutcome, SharePayload, ShareProvider } from '../types.js'

/**
 * Web Share with transient-activation handling — PRD §10.1 (PLT-01/02).
 * Prepare the payload synchronously; any async work (link creation) happens
 * before the user taps a fresh Share button. Cancel is not a delivery failure.
 */

interface NavigatorShareLike {
  share?: (data: unknown) => Promise<void>
  canShare?: (data: unknown) => boolean
}

export interface WebShareEnvironment {
  readonly navigator?: NavigatorShareLike
  readonly is_secure_context?: boolean
  /** True while a user gesture is still active; false → ask for a fresh tap. */
  readonly is_activation_active?: () => boolean
  readonly copy_text?: (text: string) => Promise<void>
}

function toShareData(payload: SharePayload): Record<string, unknown> {
  const data: Record<string, unknown> = {}
  if (payload.title !== undefined) data.title = payload.title
  if (payload.text !== undefined) data.text = payload.text
  if (payload.url !== undefined) data.url = payload.url
  if (payload.files !== undefined && payload.files.length > 0) {
    data.files = payload.files.map(
      (file) => new File([new Uint8Array(file.bytes)], file.name, { type: file.media_type }),
    )
  }
  return data
}

export function createWebShareProvider(env: WebShareEnvironment = {}): ShareProvider {
  const navigatorLike =
    env.navigator ?? (globalThis as { navigator?: NavigatorShareLike }).navigator ?? {}

  function canShare(payload: SharePayload): boolean {
    if (env.is_secure_context === false) return false
    if (navigatorLike.share === undefined) return false
    if (payload.files !== undefined && payload.files.length > 0) {
      if (typeof File === 'undefined') return false
      if (navigatorLike.canShare !== undefined) {
        try {
          if (!navigatorLike.canShare({ files: payload.files })) return false
        } catch {
          return false
        }
      }
    }
    return true
  }

  function prepare(payload: SharePayload): PreparedShare {
    return { payload, fallback: payload.url !== undefined ? 'copy_link' : 'download' }
  }

  async function share(prepared: PreparedShare): Promise<ShareOutcome> {
    if (env.is_secure_context === false) return 'unavailable'
    if (navigatorLike.share === undefined) return 'unavailable'
    if (env.is_activation_active !== undefined && !env.is_activation_active()) {
      return 'activation_lost'
    }
    if (!canShare(prepared.payload)) return 'unavailable'
    try {
      await navigatorLike.share(toShareData(prepared.payload))
      return 'shared'
    } catch (error) {
      const name = (error as { name?: unknown }).name
      if (name === 'AbortError') return 'cancelled'
      if (name === 'NotAllowedError') return 'activation_lost'
      return 'unavailable'
    }
  }

  return { canShare, prepare, share }
}

export type CopyLinkOutcome = 'copied' | 'unavailable'

export async function copyLink(
  url: string,
  env: WebShareEnvironment = {},
): Promise<CopyLinkOutcome> {
  const clipboard = (
    globalThis as { navigator?: { clipboard?: { writeText(text: string): Promise<void> } } }
  ).navigator?.clipboard
  const copy =
    env.copy_text ?? (clipboard === undefined ? undefined : (text) => clipboard.writeText(text))
  if (copy === undefined) return 'unavailable'
  try {
    await copy(url)
    return 'copied'
  } catch {
    return 'unavailable'
  }
}
